/**
 * RAG (Retrieval-Augmented Generation) Service
 * Handles document and thread embedding, storage, and semantic retrieval
 */

import { supabase, isSupabaseConfigured, getCurrentUserId } from '@/lib/supabase';
import { embeddingService } from './embeddingService';
import { embeddingJobService } from './embeddingJobService';
import { coverageIdsFromRpc, presenceForIds } from './embeddingCoverage';
import { knowledgeBaseService } from './knowledgeBaseService';
import { chatThreadsService } from './chatThreadsService';
import { mergeScopedRetrievalRows, RAG_PER_WORKSPACE_CAP } from '@/lib/ragScopePure';
import {
  dedupeScopeChunks,
  retrievalModeFor,
  SCOPE_ENTIRE_MAX_CHARS,
  selectEntireScopeChunks,
  type ScopeCoverage,
  type ScopeKind,
} from '@shared/scopeRag';

export interface DocumentChunk {
  id: string;
  documentId: string;
  chunkIndex: number;
  chunkText: string;
  embedding?: number[];
  metadata?: any;
  createdAt?: string;
  updatedAt?: string;
}

export interface RetrievalResult {
  documentId?: string;
  threadId?: string;
  chunkText: string;
  similarity: number;
  documentTitle?: string;
  threadTitle?: string;
  metadata?: any;
  /** When present, indicates source is a chat thread rather than a document */
  sourceType?: 'document' | 'thread' | 'topic' | 'space';
  chunkIndex?: number;
}

/** Topic manifest entry shape (matches contextOrchestrator topicDocumentManifest). */
export interface TopicManifestEntryForRag {
  id: string;
  title: string;
}

/** Max characters when loading full KB text for manifest title matches (≈12–15k tokens). */
const MANIFEST_DIRECT_CONTENT_MAX_CHARS = 48000;

/** How many distinct docs/threads to expand from chunks → complete bodies after RAG ranking. */
const FULL_EXPAND_MAX_DOCS = 3;
const FULL_EXPAND_MAX_THREADS = 3;

/** Prefer embedding concat when KB row body is shorter than this (avoids topic-name stubs). */
const SHORT_KB_CONTENT_CHARS = 80;

const MIN_MANIFEST_TITLE_MATCH_LEN = 3;

export interface EmbeddingStats {
  totalDocuments: number;
  totalChunks: number;
  avgChunksPerDoc: number;
  oldestEmbedding?: string;
  newestEmbedding?: string;
}

export interface ProcessingProgress {
  documentId?: string;
  documentTitle?: string;
  threadId?: string;
  threadTitle?: string;
  totalChunks: number;
  processedChunks: number;
  status: 'processing' | 'completed' | 'failed';
  error?: string;
  /** Batch embed: documents finished so far */
  completedDocs?: number;
  /** Batch embed: total documents in batch */
  totalDocs?: number;
  /** Batch embed lifecycle phase */
  phase?: 'enqueuing' | 'processing';
  /** Titles skipped because content was empty (Embed All) */
  skippedTitles?: string[];
}

class RAGService {
  private lastScopeRetrieval: ScopeCoverage | null = null;

  getLastScopeRetrieval(): ScopeCoverage | null {
    return this.lastScopeRetrieval;
  }
  private activeDocumentEmbeddings = new Map<string, Promise<void>>();
  private queuedDocumentEmbeddings = new Set<string>();
  private scheduledDocumentEmbeds = new Map<string, ReturnType<typeof setTimeout>>();
  private activeThreadEmbeddings = new Map<string, Promise<void>>();
  private queuedThreadEmbeddings = new Set<string>();
  private scheduledThreadEmbeds = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly defaultEmbedDebounceMs = 15000;
  private readonly defaultThreadEmbedDebounceMs = 30000;

  private normalizeForTitleMatch(s: string): string {
    return s.trim().toLowerCase().replace(/\s+/g, ' ');
  }

  /**
   * Manifest titles whose normalized form appears as a substring of the user message (longest titles first).
   */
  findManifestTitleMatchesInQuery(
    userMessage: string,
    manifest: TopicManifestEntryForRag[]
  ): TopicManifestEntryForRag[] {
    if (!manifest.length || !userMessage.trim()) return [];
    const msg = this.normalizeForTitleMatch(userMessage);
    const sorted = [...manifest].sort((a, b) => b.title.length - a.title.length);
    const out: TopicManifestEntryForRag[] = [];
    const seen = new Set<string>();
    for (const m of sorted) {
      const t = this.normalizeForTitleMatch(m.title);
      if (t.length < MIN_MANIFEST_TITLE_MATCH_LEN) continue;
      if (msg.includes(t)) {
        if (!seen.has(m.id)) {
          seen.add(m.id);
          out.push({ id: m.id, title: m.title });
        }
      }
    }
    return out;
  }

  /**
   * Load all chunks for a document (ordered). Used when KB row has no body but embeddings exist.
   */
  private async fetchEmbeddingChunksByDocumentId(
    documentId: string,
    userId: string
  ): Promise<RetrievalResult[]> {
    const { data, error } = await supabase
      .from('document_embeddings')
      .select('document_id, chunk_text, chunk_index, metadata')
      .eq('user_id', userId)
      .eq('document_id', documentId)
      .order('chunk_index', { ascending: true });

    if (error) {
      console.warn('[ragService] fetchEmbeddingChunksByDocumentId:', error.message);
      return [];
    }

    return (data || []).map((row: any) => ({
      documentId: row.document_id,
      chunkText: row.chunk_text,
      similarity: 0.95,
      documentTitle: row.metadata?.documentTitle,
      metadata: row.metadata,
      sourceType: 'document' as const,
    }));
  }

  /**
   * Optional: filter embeddings by stored metadata title (matches embed pipeline documentTitle field).
   */
  private async fetchEmbeddingChunksByMetadataDocumentTitle(
    documentTitle: string,
    userId: string
  ): Promise<RetrievalResult[]> {
    const { data, error } = await supabase
      .from('document_embeddings')
      .select('document_id, chunk_text, chunk_index, metadata')
      .eq('user_id', userId)
      .eq('metadata->>documentTitle', documentTitle)
      .order('chunk_index', { ascending: true });

    if (error) {
      console.warn('[ragService] metadata->>documentTitle filter failed:', error.message);
      return [];
    }

    return (data || []).map((row: any) => ({
      documentId: row.document_id,
      chunkText: row.chunk_text,
      similarity: 0.95,
      documentTitle: row.metadata?.documentTitle ?? documentTitle,
      metadata: { ...row.metadata, source: 'embedding_metadata_title' },
      sourceType: 'document' as const,
    }));
  }

  /**
   * Concatenate chunks up to maxChars (manifest direct retrieval fallback).
   */
  private concatenateChunks(results: RetrievalResult[], maxChars: number): RetrievalResult | null {
    if (!results.length) return null;
    const docId = results[0].documentId;
    const title = results[0].documentTitle;
    let text = '';
    for (const r of results) {
      if (r.documentId !== docId) break;
      if (text.length >= maxChars) break;
      text += (text ? '\n\n' : '') + r.chunkText;
    }
    if (text.length > maxChars) text = text.slice(0, maxChars) + '\n...[truncated]';
    return {
      documentId: docId,
      chunkText: text,
      similarity: 1,
      documentTitle: title,
      metadata: { source: 'manifest_title_chunks_concat', concatenated: true },
      sourceType: 'document',
    };
  }

  /**
   * Get current user ID
   */
  private async getCurrentUserId(): Promise<string> {
    if (!isSupabaseConfigured) {
      throw new Error('Service not configured');
    }

    const userId = await getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    return userId;
  }

  /**
   * Generate unique ID for embeddings
   */
  private generateId(): string {
    return `emb_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private async embedDocumentOnce(
    documentId: string,
    onProgress?: (progress: ProcessingProgress) => void,
    options?: { forceReindex?: boolean }
  ): Promise<void> {
    const document = await knowledgeBaseService.getKnowledgeBaseItem(documentId);
    if (!document) throw new Error('Document not found');
    if (!document.content?.trim()) throw new Error('Document has no content to embed');

    onProgress?.({
      documentId,
      documentTitle: document.title,
      totalChunks: 0,
      processedChunks: 0,
      status: 'processing',
    });

    try {
      await embeddingJobService.enqueueAndWait(
        [{
          job_type: 'document',
          target_id: documentId,
          ...(options?.forceReindex ? { payload: { forceReindex: true } } : {}),
        }],
        (job) => {
          const p = job.progress as {
            totalChunks?: number;
            processedChunks?: number;
            documentTitle?: string;
          } | undefined;
          onProgress?.({
            documentId,
            documentTitle: p?.documentTitle || document.title,
            totalChunks: p?.totalChunks ?? 0,
            processedChunks: p?.processedChunks ?? 0,
            status: job.status === 'completed' ? 'completed' : 'processing',
            error: job.last_error ?? undefined,
          });
        }
      );

      onProgress?.({
        documentId,
        documentTitle: document.title,
        totalChunks: 0,
        processedChunks: 0,
        status: 'completed',
      });
      console.log(`✅ Successfully embedded document: ${document.title}`);
    } catch (error) {
      onProgress?.({
        documentId,
        documentTitle: document.title || 'Unknown',
        totalChunks: 0,
        processedChunks: 0,
        status: 'failed',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Debounce auto-embed after document saves so typing does not trigger a full re-embed every 2s.
   */
  scheduleEmbedDocument(
    documentId: string,
    options: { debounceMs?: number } = {}
  ): void {
    void embeddingJobService.recoverStale(2).catch(() => {});

    const debounceMs = options.debounceMs ?? this.defaultEmbedDebounceMs;
    const existing = this.scheduledDocumentEmbeds.get(documentId);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      this.scheduledDocumentEmbeds.delete(documentId);
      this.embedDocument(documentId, undefined, { forceReindex: true }).catch(() => {});
    }, debounceMs);
    this.scheduledDocumentEmbeds.set(documentId, timer);
  }

  cancelScheduledEmbedDocument(documentId: string): void {
    const timer = this.scheduledDocumentEmbeds.get(documentId);
    if (timer) {
      clearTimeout(timer);
      this.scheduledDocumentEmbeds.delete(documentId);
    }
  }

  /**
   * Debounce auto-embed after chat thread message saves.
   */
  scheduleEmbedThread(
    threadId: string,
    options: { debounceMs?: number } = {}
  ): void {
    void embeddingJobService.recoverStale(2).catch(() => {});

    const debounceMs = options.debounceMs ?? this.defaultThreadEmbedDebounceMs;
    const existing = this.scheduledThreadEmbeds.get(threadId);
    if (existing) clearTimeout(existing);
    const timer = setTimeout(() => {
      this.scheduledThreadEmbeds.delete(threadId);
      this.embedThread(threadId, undefined, { forceReindex: true }).catch(() => {});
    }, debounceMs);
    this.scheduledThreadEmbeds.set(threadId, timer);
  }

  cancelScheduledEmbedThread(threadId: string): void {
    const timer = this.scheduledThreadEmbeds.get(threadId);
    if (timer) {
      clearTimeout(timer);
      this.scheduledThreadEmbeds.delete(threadId);
    }
  }

  /**
   * Process and embed a single document
   */
  async embedDocument(
    documentId: string,
    onProgress?: (progress: ProcessingProgress) => void,
    options?: { forceReindex?: boolean }
  ): Promise<void> {
    const inFlight = this.activeDocumentEmbeddings.get(documentId);
    if (inFlight) {
      this.queuedDocumentEmbeddings.add(documentId);
      return inFlight;
    }

    const task = (async () => {
      try {
        do {
          this.queuedDocumentEmbeddings.delete(documentId);
          await this.embedDocumentOnce(documentId, onProgress, options);
        } while (this.queuedDocumentEmbeddings.has(documentId));
      } finally {
        this.activeDocumentEmbeddings.delete(documentId);
        this.queuedDocumentEmbeddings.delete(documentId);
      }
    })();

    this.activeDocumentEmbeddings.set(documentId, task);
    return task;
  }

  /**
   * Process and embed multiple documents.
   * Empty documents are skipped (reported via skippedTitles) and do not fail the batch.
   */
  async embedDocuments(
    documentIds: string[],
    onProgress?: (progress: ProcessingProgress) => void,
    options?: { forceReindex?: boolean }
  ): Promise<{ completedDocs: number; failedWaves: number; skippedTitles: string[] }> {
    if (documentIds.length === 0) {
      return { completedDocs: 0, failedWaves: 0, skippedTitles: [] };
    }

    const WAVE_SIZE = 20;
    const totalDocs = documentIds.length;
    let completedDocs = 0;
    let failedWaves = 0;
    const skippedTitles: string[] = [];

    onProgress?.({
      totalDocs,
      completedDocs: 0,
      totalChunks: 0,
      processedChunks: 0,
      status: 'processing',
      phase: 'enqueuing',
      documentTitle: `Checking ${totalDocs} document${totalDocs === 1 ? '' : 's'}…`,
    });

    for (let waveStart = 0; waveStart < documentIds.length; waveStart += WAVE_SIZE) {
      const waveIds = documentIds.slice(waveStart, waveStart + WAVE_SIZE);
      const waveIndex = Math.floor(waveStart / WAVE_SIZE) + 1;
      const waveCount = Math.ceil(documentIds.length / WAVE_SIZE);
      const waveCompletedIds = new Set<string>();

      const loaded = await Promise.all(
        waveIds.map((id) => knowledgeBaseService.getKnowledgeBaseItem(id))
      );

      const toEmbed: string[] = [];
      const waveSkipped: string[] = [];

      for (let i = 0; i < waveIds.length; i++) {
        const id = waveIds[i];
        const doc = loaded[i];
        const title = (doc?.title && String(doc.title).trim()) || id;
        const content = typeof doc?.content === 'string' ? doc.content : '';
        if (!doc || !content.trim()) {
          waveSkipped.push(title);
          skippedTitles.push(title);
          continue;
        }
        toEmbed.push(id);
      }

      if (waveSkipped.length > 0) {
        onProgress?.({
          totalDocs,
          completedDocs,
          totalChunks: 0,
          processedChunks: 0,
          status: 'processing',
          phase: 'processing',
          documentTitle:
            waveSkipped.length === 1
              ? `Skipped "${waveSkipped[0]}": document is empty`
              : `Skipped ${waveSkipped.length} empty document(s)`,
          skippedTitles: [...skippedTitles],
        });
      }

      if (toEmbed.length === 0) {
        completedDocs += waveIds.length;
        continue;
      }

      const jobs = toEmbed.map((id) => ({
        job_type: 'document' as const,
        target_id: id,
        ...(options?.forceReindex ? { payload: { forceReindex: true } } : {}),
      }));

      try {
        await embeddingJobService.enqueueAndWait(
          jobs,
          (job) => {
            if (job.job_type !== 'document') return;
            const p = job.progress as {
              documentId?: string;
              documentTitle?: string;
              totalChunks?: number;
              processedChunks?: number;
              status?: string;
              skipReason?: string;
            } | undefined;

            if (job.status === 'completed') {
              waveCompletedIds.add(job.id);
            }

            if (
              job.status === 'cancelled' &&
              (p?.status === 'skipped' || /document is empty|no chunks generated/i.test(job.last_error ?? ''))
            ) {
              const skipTitle =
                (p?.documentTitle && String(p.documentTitle).trim()) || job.target_id;
              if (!skippedTitles.includes(skipTitle)) {
                skippedTitles.push(skipTitle);
              }
            }

            onProgress?.({
              documentId: job.target_id,
              documentTitle: p?.documentTitle,
              totalDocs,
              completedDocs: completedDocs + waveCompletedIds.size + waveSkipped.length,
              totalChunks: p?.totalChunks ?? 0,
              processedChunks: p?.processedChunks ?? 0,
              status: job.status === 'completed' ? 'completed' : 'processing',
              phase: 'processing',
              error: job.last_error ?? undefined,
              skippedTitles: [...skippedTitles],
            });
          },
          { maxWaitMs: toEmbed.length * 90_000 }
        );
        completedDocs += waveIds.length;
      } catch (error) {
        failedWaves += 1;
        // Count pre-filtered skips + any jobs that completed before the failure
        completedDocs += waveSkipped.length + waveCompletedIds.size;
        console.error(
          `Failed to embed wave ${waveIndex}/${waveCount} (${toEmbed.length} docs):`,
          error
        );
        onProgress?.({
          totalDocs,
          completedDocs,
          totalChunks: 0,
          processedChunks: 0,
          status: 'processing',
          phase: 'processing',
          documentTitle: `Wave ${waveIndex}/${waveCount} incomplete — continuing…`,
          error: error instanceof Error ? error.message : String(error),
          skippedTitles: [...skippedTitles],
        });
      }
    }

    onProgress?.({
      totalDocs,
      completedDocs,
      totalChunks: 0,
      processedChunks: 0,
      status: failedWaves > 0 && completedDocs < totalDocs ? 'failed' : 'completed',
      phase: 'processing',
      skippedTitles: [...skippedTitles],
    });

    if (failedWaves > 0 && completedDocs < totalDocs) {
      throw new Error(
        `Embedded ${completedDocs} of ${totalDocs} documents (${failedWaves} wave(s) incomplete)`
      );
    }

    return { completedDocs, failedWaves, skippedTitles };
  }

  /**
   * Queue document embed jobs and return. The settings drain loop processes them.
   */
  async enqueueDocumentsForEmbedding(documentIds: string[]): Promise<number> {
    const unique = [...new Set(documentIds.filter(Boolean))];
    if (unique.length === 0) return 0;

    const CHUNK = 40;
    let queued = 0;
    for (let i = 0; i < unique.length; i += CHUNK) {
      const slice = unique.slice(i, i + CHUNK);
      const jobs = await embeddingJobService.enqueue(
        slice.map((id) => ({ job_type: 'document' as const, target_id: id }))
      );
      queued += jobs.length;
    }
    return queued;
  }

  /**
   * Embed all documents in a space
   */
  async embedSpaceDocuments(
    spaceId: string,
    onProgress?: (progress: ProcessingProgress) => void
  ): Promise<void> {
    const documents = await knowledgeBaseService.getKnowledgeBaseItems(spaceId);
    const documentIds = documents
      .filter(doc => doc.content && doc.content.trim().length > 0)
      .map(doc => doc.id);

    await this.embedDocuments(documentIds, onProgress);
  }

  /**
   * Delete embeddings for a document
   */
  async deleteDocumentEmbeddings(documentId: string): Promise<void> {
    const userId = await this.getCurrentUserId();

    const { error } = await supabase
      .from('document_embeddings')
      .delete()
      .eq('document_id', documentId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error deleting embeddings:', error);
      throw new Error(`Failed to delete embeddings: ${error.message}`);
    }
  }

  /**
   * Build plain text from thread messages for embedding (role: content per message)
   */
  private threadMessagesToText(messages: Array<{ role?: string; content?: string }> | null | undefined): string {
    if (!Array.isArray(messages) || messages.length === 0) return '';
    return messages
      .map((m) => {
        const role = (m.role || 'user').toLowerCase();
        const content = typeof m.content === 'string' ? m.content : '';
        return `${role}: ${content}`.trim();
      })
      .filter(Boolean)
      .join('\n\n');
  }

  /**
   * Process and embed a single chat thread (messages → chunks → vectors in thread_embeddings)
   */
  async embedThread(
    threadId: string,
    onProgress?: (progress: ProcessingProgress) => void,
    options?: { forceReindex?: boolean }
  ): Promise<void> {
    const thread = await chatThreadsService.getChatThread(threadId);
    if (!thread) throw new Error('Thread not found');

    const text = this.threadMessagesToText(thread.messages);
    if (!text?.trim()) throw new Error('Thread has no message content to embed');

    const threadTitle = thread.title || 'Untitled conversation';

    onProgress?.({
      threadId,
      threadTitle,
      totalChunks: 0,
      processedChunks: 0,
      status: 'processing',
    });

    try {
      await embeddingJobService.enqueueAndWait(
        [{
          job_type: 'thread',
          target_id: threadId,
          ...(options?.forceReindex ? { payload: { forceReindex: true } } : {}),
        }],
        (job) => {
          const p = job.progress as { totalChunks?: number; processedChunks?: number } | undefined;
          onProgress?.({
            threadId,
            threadTitle,
            totalChunks: p?.totalChunks ?? 0,
            processedChunks: p?.processedChunks ?? 0,
            status: job.status === 'completed' ? 'completed' : 'processing',
            error: job.last_error ?? undefined,
          });
        }
      );

      onProgress?.({
        threadId,
        threadTitle,
        totalChunks: 0,
        processedChunks: 0,
        status: 'completed',
      });
    } catch (err) {
      onProgress?.({
        threadId,
        threadTitle,
        totalChunks: 0,
        processedChunks: 0,
        status: 'failed',
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  }

  /**
   * Embed all threads linked to a topic
   */
  async embedThreadsForTopic(
    topicId: string,
    onProgress?: (progress: ProcessingProgress) => void
  ): Promise<void> {
    const threads = await chatThreadsService.getThreadSummariesByTopic(topicId);
    for (let i = 0; i < threads.length; i++) {
      const t = threads[i];
      try {
        await this.embedThread(t.id, (p) => {
          onProgress?.({
            ...p,
            totalChunks: threads.length,
            processedChunks: i + (p.status === 'completed' ? 1 : 0),
          });
        });
      } catch (e) {
        console.error(`Failed to embed thread ${t.id}:`, e);
      }
    }
  }

  /**
   * Delete all embeddings for a thread
   */
  async deleteThreadEmbeddings(threadId: string): Promise<void> {
    const userId = await this.getCurrentUserId();
    const { error } = await supabase
      .from('thread_embeddings')
      .delete()
      .eq('thread_id', threadId)
      .eq('user_id', userId);
    if (error) {
      console.error('Error deleting thread embeddings:', error);
      throw new Error(`Failed to delete thread embeddings: ${error.message}`);
    }
  }

  /**
   * Check if a thread has embeddings
   */
  async hasThreadEmbeddings(threadId: string): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    const { count, error } = await supabase
      .from('thread_embeddings')
      .select('*', { count: 'exact', head: true })
      .eq('thread_id', threadId)
      .eq('user_id', userId);
    if (error) return false;
    return (count || 0) > 0;
  }

  /**
   * Get embedding status for multiple threads (e.g. for Topics tab)
   */
  async getThreadEmbeddingStatusForThreads(threadIds: string[]): Promise<Record<string, boolean>> {
    if (threadIds.length === 0) return {};
    const userId = await this.getCurrentUserId();
    return presenceForIds(threadIds, async (batch) => {
      const { data, error } = await supabase.rpc('embedding_ids_present', {
        p_user_id: userId,
        p_kind: 'thread',
        p_ids: batch,
      });
      if (error) {
        console.error('Error fetching thread embedding status:', error);
        return [];
      }
      return coverageIdsFromRpc(data);
    });
  }

  /**
   * Full-text search on chunk_text (BM25-style via Postgres FTS). Used for hybrid search.
   */
  private async searchFts(
    query: string,
    options: { spaceId?: string; topicId?: string; limit?: number }
  ): Promise<RetrievalResult[]> {
    const { spaceId, topicId, limit = 10 } = options;
    const userId = await this.getCurrentUserId();

    const { data, error } = await supabase.rpc('search_embeddings_fts', {
      query_text: query.trim(),
      filter_user_id: userId,
      filter_space_id: spaceId || null,
      match_count: limit,
      filter_topic_id: topicId || null,
    });

    if (error) {
      console.warn('FTS search failed (hybrid may fall back to vector only):', error);
      return [];
    }

    return (data || []).map((row: { document_id: string; chunk_text: string; rank: number; metadata?: any }) => ({
      documentId: row.document_id,
      chunkText: row.chunk_text,
      similarity: row.rank,
      documentTitle: row.metadata?.documentTitle,
      metadata: row.metadata,
    }));
  }

  /**
   * Reciprocal rank fusion (RRF) to merge vector and FTS results. k=60 is standard.
   */
  private mergeWithRRF(
    vectorResults: RetrievalResult[],
    ftsResults: RetrievalResult[],
    k: number = 60
  ): RetrievalResult[] {
    const scoreMap = new Map<string, { result: RetrievalResult; score: number }>();

    const add = (results: RetrievalResult[], rankOffset: number) => {
      results.forEach((r, i) => {
        const rank = rankOffset + i + 1;
        const key = `${r.documentId}\n${r.chunkText}`;
        const rrf = 1 / (k + rank);
        const existing = scoreMap.get(key);
        if (existing) {
          existing.score += rrf;
        } else {
          scoreMap.set(key, { result: r, score: rrf });
        }
      });
    };

    add(vectorResults, 0);
    add(ftsResults, 0);

    return Array.from(scoreMap.values())
      .sort((a, b) => b.score - a.score)
      .map(({ result }) => result);
  }

  /**
   * Call rerank API and reorder results. On 501 or error, returns original order.
   */
  private async applyRerank(
    query: string,
    results: RetrievalResult[],
    topN: number
  ): Promise<RetrievalResult[]> {
    if (results.length === 0) return results;
    try {
      const res = await fetch('/api/rerank', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: query.trim(),
          documents: results.map((r) => r.chunkText),
          topN,
        }),
      });
      if (res.status === 501) return results;
      if (!res.ok) return results;
      const data = (await res.json()) as { results?: Array<{ index: number; relevance_score: number }> };
      const order = data.results;
      if (!order?.length) return results;
      const reordered: RetrievalResult[] = [];
      for (const { index } of order) {
        if (index >= 0 && index < results.length) reordered.push(results[index]);
      }
      return reordered.length ? reordered : results;
    } catch {
      return results;
    }
  }

  private missingScopeRpc(error: { message?: string; code?: string } | null | undefined): boolean {
    const message = error?.message ?? '';
    return error?.code === 'PGRST202' || /could not find the function|schema cache/i.test(message);
  }

  /**
   * Topic or space scope. Child vectors are filtered by resolve_* RPCs.
   * Returns null when the migration is not applied so the caller can use the previous path.
   */
  private async searchResolvedScope(
    query: string,
    options: {
      spaceId?: string;
      topicId?: string;
      limit?: number;
      similarityThreshold?: number;
      scopeBreadth?: 'retrieval' | 'entire';
    }
  ): Promise<RetrievalResult[] | null> {
    const started = Date.now();
    const scopeType: ScopeKind = options.topicId ? 'topic' : 'space';
    const scopeId = options.topicId || options.spaceId || '';
    const breadth = options.scopeBreadth ?? 'retrieval';
    const limit = options.limit ?? 10;
    const similarityThreshold = options.similarityThreshold ?? 0.5;
    if (!scopeId) return [];

    const { embedding } = await embeddingService.generateEmbedding(query);
    const queryEmbedding = JSON.stringify(embedding);

    const mapRow = (row: {
      source_type: string;
      source_id: string;
      chunk_index: number;
      chunk_text: string;
      similarity: number;
      title?: string;
    }): RetrievalResult => ({
      documentId: row.source_type === 'document' ? row.source_id : undefined,
      threadId: row.source_type === 'thread' ? row.source_id : undefined,
      chunkText: row.chunk_text,
      similarity: row.similarity,
      documentTitle: row.source_type === 'document' ? row.title : undefined,
      threadTitle: row.source_type === 'thread' ? row.title : undefined,
      sourceType: row.source_type as RetrievalResult['sourceType'],
      chunkIndex: row.chunk_index,
      metadata: {
        citationIdentity: `${row.source_type}:${row.source_id}/chunk:${row.chunk_index}`,
        ...(scopeType === 'topic' ? { topicId: scopeId } : { spaceId: scopeId }),
      },
    });

    const orientation = async (): Promise<RetrievalResult[]> => {
      const { data, error } = await supabase.rpc('match_scope_orientation', {
        query_embedding: queryEmbedding,
        p_scope_type: scopeType,
        p_scope_id: scopeId,
      });
      if (error) {
        if (!this.missingScopeRpc(error)) {
          console.warn('[rag] scope orientation omitted', { scope_type: scopeType, scope_id: scopeId });
        }
        return [];
      }
      const row = (data ?? [])[0] as { chunk_text?: string; similarity?: number; identity?: string; source_type?: string } | undefined;
      if (!row?.chunk_text) return [];
      return [{
        chunkText: row.chunk_text,
        similarity: row.similarity ?? 0,
        sourceType: scopeType,
        chunkIndex: 0,
        documentTitle: scopeType === 'topic' ? 'Topic' : 'Space',
        metadata: {
          citationIdentity: row.identity ?? `${scopeType}:${scopeId}`,
          orientation: true,
          ...(scopeType === 'topic' ? { topicId: scopeId } : { spaceId: scopeId }),
        },
      }];
    };

    if (breadth === 'entire') {
      const statsRes = await supabase.rpc('scope_rag_stats', {
        p_scope_type: scopeType,
        p_scope_id: scopeId,
      });
      if (statsRes.error) {
        if (this.missingScopeRpc(statsRes.error)) return null;
        throw new Error(statsRes.error.message);
      }
      const stats = (Array.isArray(statsRes.data) ? statsRes.data[0] : statsRes.data) as {
        authorized?: boolean;
        threads_count?: number;
        documents_count?: number;
        indexed_chunks_count?: number;
      } | null;
      if (!stats?.authorized) {
        this.lastScopeRetrieval = {
          mode: 'entire',
          complete: false,
          resources_total: 0,
          resources_included: 0,
          chunks_total: 0,
          chunks_included: 0,
          scope_type: scopeType,
          scope_id: scopeId,
          resources_resolved: 0,
          chunks_retrieved: 0,
          chunks_deduplicated: 0,
          retrieval_ms: Date.now() - started,
        };
        console.info('[rag] scope retrieval', this.lastScopeRetrieval);
        return [];
      }

      const fetched = await supabase.rpc('fetch_scope_chunks', {
        query_embedding: queryEmbedding,
        p_scope_type: scopeType,
        p_scope_id: scopeId,
        p_per_resource: 12,
        p_max_rows: 200,
      });
      if (fetched.error) {
        if (this.missingScopeRpc(fetched.error)) return null;
        throw new Error(fetched.error.message);
      }
      const rows = (fetched.data ?? []) as Array<{
        source_type: string;
        source_id: string;
        chunk_index: number;
        chunk_text: string;
        similarity: number;
        title?: string;
        chunks_total?: number;
      }>;
      const { chunks: deduped, removed } = dedupeScopeChunks(rows.map((row) => ({
        sourceType: row.source_type,
        sourceId: row.source_id,
        chunkIndex: row.chunk_index,
        chunkText: row.chunk_text,
        similarity: row.similarity,
        title: row.title,
      })));
      const chunksTotal = rows[0]?.chunks_total ?? stats.indexed_chunks_count ?? deduped.length;
      const resourcesTotal = (stats.threads_count ?? 0) + (stats.documents_count ?? 0);
      const packed = selectEntireScopeChunks({
        chunks: deduped,
        chunksTotal,
        resourcesTotal,
        maxChars: SCOPE_ENTIRE_MAX_CHARS,
      });
      const orient = await orientation();
      const retrievalMode = retrievalModeFor({ scope: scopeType, breadth: 'entire', complete: packed.coverage.complete });
      this.lastScopeRetrieval = {
        ...packed.coverage,
        retrievalMode,
        scope_type: scopeType,
        scope_id: scopeId,
        resources_resolved: resourcesTotal,
        chunks_retrieved: rows.length,
        chunks_deduplicated: removed,
        retrieval_ms: Date.now() - started,
      };
      console.info('[rag] scope retrieval', this.lastScopeRetrieval);
      return [
        ...orient,
        ...packed.selected.map((chunk) => mapRow({
          source_type: chunk.sourceType,
          source_id: chunk.sourceId,
          chunk_index: chunk.chunkIndex,
          chunk_text: chunk.chunkText,
          similarity: chunk.similarity,
          title: (chunk as { title?: string }).title,
        })),
      ];
    }

    const matched = await supabase.rpc('match_resolved_scope', {
      query_embedding: queryEmbedding,
      p_scope_type: scopeType,
      p_scope_id: scopeId,
      match_threshold: similarityThreshold,
      match_count: limit,
    });
    if (matched.error) {
      if (this.missingScopeRpc(matched.error)) return null;
      throw new Error(matched.error.message);
    }
    const rows = (matched.data ?? []) as Array<{
      source_type: string;
      source_id: string;
      chunk_index: number;
      chunk_text: string;
      similarity: number;
      title?: string;
    }>;
    const { chunks: deduped, removed } = dedupeScopeChunks(rows.map((row) => ({
      sourceType: row.source_type,
      sourceId: row.source_id,
      chunkIndex: row.chunk_index,
      chunkText: row.chunk_text,
      similarity: row.similarity,
      title: row.title,
    })));
    const orient = await orientation();
    const statsRes = await supabase.rpc('scope_rag_stats', {
      p_scope_type: scopeType,
      p_scope_id: scopeId,
    });
    const stats = (!statsRes.error && Array.isArray(statsRes.data) ? statsRes.data[0] : null) as {
      threads_count?: number;
      documents_count?: number;
    } | null;
    const resourcesResolved = (stats?.threads_count ?? 0) + (stats?.documents_count ?? 0);
    this.lastScopeRetrieval = {
      mode: 'retrieval',
      complete: false,
      resources_total: resourcesResolved,
      resources_included: new Set(deduped.map((chunk) => `${chunk.sourceType}:${chunk.sourceId}`)).size,
      chunks_total: deduped.length,
      chunks_included: deduped.length,
      retrievalMode: retrievalModeFor({ scope: scopeType, breadth: 'retrieval', complete: false }),
      scope_type: scopeType,
      scope_id: scopeId,
      resources_resolved: resourcesResolved,
      chunks_retrieved: rows.length,
      chunks_deduplicated: removed,
      retrieval_ms: Date.now() - started,
    };
    console.info('[rag] scope retrieval', this.lastScopeRetrieval);
    return [
      ...orient,
      ...deduped.map((chunk) => mapRow({
        source_type: chunk.sourceType,
        source_id: chunk.sourceId,
        chunk_index: chunk.chunkIndex,
        chunk_text: chunk.chunkText,
        similarity: chunk.similarity,
        title: (chunk as { title?: string }).title,
      })),
    ];
  }

  /**
   * Semantic search using vector similarity. Optionally run hybrid (vector + FTS) and merge with RRF.
   * When useRerank is true, top candidates are reranked via Cohere (if configured).
   * When includeThreads is true and topicId/spaceId is set, also retrieves from embedded chat threads and merges.
   */
  async search(
    query: string,
    options: {
      spaceId?: string;
      topicId?: string;
      limit?: number;
      similarityThreshold?: number;
      /** When true, also run full-text search and merge with vector results via RRF */
      hybridSearch?: boolean;
      /** When true, rerank top candidates via Cohere (requires COHERE_API_KEY). */
      useRerank?: boolean;
      /** When true, also search embedded chat threads (filtered by spaceId/topicId if provided) */
      includeThreads?: boolean;
      /**
       * Topic/space document manifest (KB titles). When the user message mentions a title, we boost keyword search
       * and load full document text (up to MANIFEST_DIRECT_CONTENT_MAX_CHARS) for that document.
       */
      topicDocumentManifest?: TopicManifestEntryForRag[];
      /** Thread titles in Knowledge scope — same direct-by-title behavior as documents. */
      threadManifest?: TopicManifestEntryForRag[];
      /**
       * DS EDIT 5 — multi-workspace RAG scope. When set, ALSO retrieves from these
       * workspaces via match_documents_scoped (membership enforced in the DB,
       * fail-closed). The personal lane above always runs unchanged.
       */
      workspaceIds?: string[];
      /** retrieval = similarity inside the scope. entire = budgeted coverage of the scope. */
      scopeBreadth?: 'retrieval' | 'entire';
    } = {}
  ): Promise<RetrievalResult[]> {
    const {
      spaceId,
      topicId,
      limit = 10,
      similarityThreshold = 0.5,
      hybridSearch = false,
      useRerank = false,
      includeThreads = false,
      topicDocumentManifest = [],
      threadManifest = [],
      workspaceIds = [],
      scopeBreadth = 'retrieval',
    } = options;

    this.lastScopeRetrieval = null;

    if (!query || query.trim().length === 0) {
      return [];
    }

    if (spaceId || topicId) {
      const scoped = await this.searchResolvedScope(query, {
        spaceId,
        topicId,
        limit,
        similarityThreshold,
        scopeBreadth,
      });
      if (scoped !== null) return scoped;
    }

    const userId = await this.getCurrentUserId();
    const { embedding } = await embeddingService.generateEmbedding(query);

    const fetchCount = useRerank ? Math.max(limit * 2, 20) : hybridSearch ? Math.max(limit * 2, 20) : limit;

    const { data: vectorData, error } = await supabase.rpc('match_documents', {
      query_embedding: JSON.stringify(embedding),
      match_threshold: similarityThreshold,
      match_count: fetchCount,
      filter_user_id: userId,
      filter_space_id: spaceId || null,
      filter_topic_id: topicId || null,
    });

    if (error) {
      console.error('Error performing semantic search:', error);
      throw new Error(`Semantic search failed: ${error.message}`);
    }

    let vectorResults: RetrievalResult[] = (vectorData || []).map((row: any) => ({
      documentId: row.document_id,
      chunkText: row.chunk_text,
      similarity: row.similarity,
      documentTitle: row.metadata?.documentTitle,
      metadata: row.metadata,
      sourceType: 'document' as const,
    }));

    // DS EDIT 5 — workspace fan-out. Fail-closed: an RPC error aborts the search
    // rather than silently degrading to single-workspace (data-leak-safe default
    // is fewer results, never unauthorized ones; the RPC itself only returns
    // rows from workspaces the caller is currently a member of).
    if (workspaceIds.length > 0) {
      const { data: scopedData, error: scopedError } = await supabase.rpc('match_documents_scoped', {
        query_embedding: JSON.stringify(embedding),
        match_threshold: similarityThreshold,
        match_count: fetchCount,
        filter_workspace_ids: workspaceIds,
        per_workspace_cap: RAG_PER_WORKSPACE_CAP,
      });
      if (scopedError) {
        console.error('Error performing scoped workspace search:', scopedError);
        throw new Error(`Workspace-scoped search failed: ${scopedError.message}`);
      }
      const scopedResults: RetrievalResult[] = (scopedData || []).map((row: any) => ({
        documentId: row.document_id,
        chunkText: row.chunk_text,
        similarity: row.similarity,
        documentTitle: row.metadata?.documentTitle,
        metadata: { ...row.metadata, scopeWorkspaceId: row.workspace_id },
        sourceType: 'document' as const,
      }));
      vectorResults = mergeScopedRetrievalRows(vectorResults, scopedResults).slice(0, fetchCount);
    }

    let ordered: RetrievalResult[];
    if (!hybridSearch) {
      ordered = vectorResults.slice(0, useRerank ? fetchCount : limit);
    } else {
      const ftsResults = await this.searchFts(query, { spaceId, topicId, limit: Math.max(limit * 2, 20) });
      ordered = this.mergeWithRRF(vectorResults, ftsResults);
      if (!useRerank) {
        ordered = ordered.slice(0, limit);
      }
    }

    // --- Direct-by-name: manifest title in user query → full KB text + keyword (FTS) on title ---
    const manifestMatches = this.findManifestTitleMatchesInQuery(query, topicDocumentManifest);
    const directByTitle: RetrievalResult[] = [];
    const directDocIds = new Set<string>();

    if (manifestMatches.length > 0) {
      for (const m of manifestMatches) {
        let fullText: string | null = null;
        let docTitle = m.title;
        try {
          const kbDoc = await knowledgeBaseService.getKnowledgeBaseItem(m.id);
          if (kbDoc?.content && kbDoc.content.trim().length > 0) {
            fullText = kbDoc.content;
            docTitle = kbDoc.title || m.title;
          }
        } catch (e) {
          console.warn(`[ragService] getKnowledgeBaseItem failed for manifest doc ${m.id}:`, e);
        }

        if (fullText) {
          if (fullText.length > MANIFEST_DIRECT_CONTENT_MAX_CHARS) {
            fullText = fullText.slice(0, MANIFEST_DIRECT_CONTENT_MAX_CHARS) + '\n...[truncated]';
          }
          directByTitle.push({
            documentId: m.id,
            chunkText: fullText,
            similarity: 1,
            documentTitle: docTitle,
            metadata: {
              source: 'manifest_title_direct',
              manifestTitleMatch: true,
              fullContentExpanded: true,
              documentTitle: docTitle,
            },
            sourceType: 'document',
          });
          directDocIds.add(m.id);
        } else {
          let embRows = await this.fetchEmbeddingChunksByDocumentId(m.id, userId);
          if (embRows.length === 0) {
            embRows = await this.fetchEmbeddingChunksByMetadataDocumentTitle(m.title, userId);
          }
          const concat = this.concatenateChunks(embRows, MANIFEST_DIRECT_CONTENT_MAX_CHARS);
          if (concat) {
            directByTitle.push({
              ...concat,
              documentId: m.id,
              metadata: {
                ...concat.metadata,
                source: 'manifest_title_embedding_concat',
                manifestTitleMatch: true,
                fullContentExpanded: true,
              },
            });
            directDocIds.add(m.id);
          }
        }

        // Keyword boost: FTS on the manifest title (filename / title terms)
        const titleFts = await this.searchFts(m.title, {
          spaceId,
          topicId,
          limit: Math.max(12, limit),
        });
        if (titleFts.length) {
          ordered = this.mergeWithRRF(ordered, titleFts);
        }
      }

      // Drop small vector/FTS chunks for documents we already loaded in full
      if (directDocIds.size > 0) {
        ordered = ordered.filter((r) => !r.documentId || !directDocIds.has(r.documentId));
      }
    }

    // Prepend full-document rows; expand output cap so direct rows are not truncated by the default limit
    ordered = [...directByTitle, ...ordered];
    const outputCap =
      directByTitle.length > 0 ? Math.min(60, limit + directByTitle.length) : limit;

    if (includeThreads) {
      // Topic scope: filter by topic only. Passing both space+topic ANDs denormalized
      // te.space_id and drops threads embedded with space_id null (still in inventory).
      const threadFilterSpaceId = topicId ? null : spaceId || null;
      const threadFilterTopicId = topicId || null;
      const { data: threadData, error: threadError } = await supabase.rpc('match_threads', {
        query_embedding: JSON.stringify(embedding),
        filter_user_id: userId,
        filter_space_id: threadFilterSpaceId,
        filter_topic_id: threadFilterTopicId,
        match_threshold: similarityThreshold,
        match_count: fetchCount,
      });
      let hadThreadHits = false;
      if (!threadError && threadData?.length) {
        hadThreadHits = true;
        const threadResults: RetrievalResult[] = (threadData || []).map((row: any) => ({
          threadId: row.thread_id,
          chunkText: row.chunk_text,
          similarity: row.similarity,
          threadTitle: row.metadata?.threadTitle,
          metadata: row.metadata,
          sourceType: 'thread' as const,
        }));
        const threadSliceCap = useRerank ? fetchCount : outputCap;
        ordered = this.mergeBySimilarity(ordered, threadResults).slice(0, threadSliceCap);
      }

      // Direct-by-title for threads in Knowledge inventory
      const threadTitleMatches = this.findManifestTitleMatchesInQuery(query, threadManifest);
      for (const m of threadTitleMatches) {
        if (ordered.some((r) => r.threadId === m.id && r.metadata?.fullContentExpanded)) continue;
        const full = await this.loadFullThreadResult(m.id, 1);
        if (full) {
          hadThreadHits = true;
          ordered = [full, ...ordered.filter((r) => r.threadId !== m.id)];
        }
      }

      // Fallback: scoped inventory + no thread hits → score titles vs query and load full bodies
      const hasThreadExpanded = ordered.some((r) => r.threadId && r.metadata?.fullContentExpanded);
      if (
        !hadThreadHits &&
        !hasThreadExpanded &&
        threadManifest.length > 0 &&
        !!(spaceId || topicId)
      ) {
        let scored = this.scoreManifestTitlesAgainstQuery(query, threadManifest).slice(
          0,
          FULL_EXPAND_MAX_THREADS
        );
        if (scored.length === 0 && this.isScopeOverviewQuery(query)) {
          scored = threadManifest.slice(0, FULL_EXPAND_MAX_THREADS);
        }
        for (const m of scored) {
          const full = await this.loadFullThreadResult(m.id, 0.85);
          if (full) {
            ordered = [full, ...ordered.filter((r) => r.threadId !== m.id)];
          }
        }
      }
    }

    // Fallback: scoped doc inventory + no document hits → score titles and load full docs
    const hasDocHit = ordered.some((r) => r.documentId && !r.threadId);
    if (
      !hasDocHit &&
      topicDocumentManifest.length > 0 &&
      !!(spaceId || topicId)
    ) {
      let scoredDocs = this.scoreManifestTitlesAgainstQuery(query, topicDocumentManifest).slice(
        0,
        FULL_EXPAND_MAX_DOCS
      );
      if (scoredDocs.length === 0 && this.isScopeOverviewQuery(query)) {
        scoredDocs = topicDocumentManifest.slice(0, FULL_EXPAND_MAX_DOCS);
      }
      for (const m of scoredDocs) {
        const full = await this.loadFullDocumentResult(m.id, 0.85);
        if (full) {
          ordered = [full, ...ordered.filter((r) => r.documentId !== m.id)];
        }
      }
    }

    if (useRerank && ordered.length > 0) {
      ordered = await this.applyRerank(query, ordered, Math.max(outputCap, FULL_EXPAND_MAX_DOCS + FULL_EXPAND_MAX_THREADS));
    }

    // Expand top-ranked chunk hits into complete document / thread bodies
    ordered = await this.expandTopHitsToFullContent(ordered, {
      maxDocs: FULL_EXPAND_MAX_DOCS,
      maxThreads: FULL_EXPAND_MAX_THREADS,
    });

    const expandCap = Math.min(
      60,
      Math.max(outputCap, ordered.filter((r) => r.metadata?.fullContentExpanded).length + limit)
    );
    return ordered.slice(0, expandCap);
  }

  /**
   * Rank inventory titles by token / substring overlap with the user query.
   * Used when vector search returns zero hits but the Knowledge inventory has items.
   */
  private scoreManifestTitlesAgainstQuery(
    query: string,
    manifest: TopicManifestEntryForRag[]
  ): TopicManifestEntryForRag[] {
    const q = this.normalizeForTitleMatch(query);
    if (!q || !manifest.length) return [];
    const tokens = q.split(/[^a-z0-9]+/).filter((t) => t.length >= 3);
    const scored = manifest.map((m) => {
      const t = this.normalizeForTitleMatch(m.title);
      let score = 0;
      if (t.length >= 3 && q.includes(t)) score += 10;
      if (t.length >= 3 && t.includes(q) && q.length >= 4) score += 8;
      for (const tok of tokens) {
        if (t.includes(tok)) score += 1;
      }
      return { m, score };
    });
    return scored
      .filter((s) => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((s) => s.m);
  }

  /** Broad questions about scope contents (list / summarize / what’s in …). */
  private isScopeOverviewQuery(query: string): boolean {
    const q = this.normalizeForTitleMatch(query);
    return /\b(summariz|summary|list|what('?s| is| are)? (in|about)|contents?|threads?|documents?|tell me about|overview)\b/.test(
      q
    );
  }

  /**
   * Replace top unique document/thread chunk hits with complete KB / message bodies.
   */
  private async expandTopHitsToFullContent(
    results: RetrievalResult[],
    options: { maxDocs: number; maxThreads: number }
  ): Promise<RetrievalResult[]> {
    if (!results.length) return results;

    const expandedDocs = new Set<string>();
    const expandedThreads = new Set<string>();
    const fullRows: RetrievalResult[] = [];
    const skipDocIds = new Set<string>();
    const skipThreadIds = new Set<string>();

    // Preserve already-expanded full bodies (manifest direct / prior expand)
    for (const r of results) {
      if (r.documentId && r.metadata?.fullContentExpanded && !expandedDocs.has(r.documentId)) {
        fullRows.push(r);
        expandedDocs.add(r.documentId);
        skipDocIds.add(r.documentId);
      }
      if (r.threadId && r.metadata?.fullContentExpanded && !expandedThreads.has(r.threadId)) {
        fullRows.push(r);
        expandedThreads.add(r.threadId);
        skipThreadIds.add(r.threadId);
      }
    }

    for (const r of results) {
      if (
        r.documentId &&
        !r.threadId &&
        !expandedDocs.has(r.documentId) &&
        expandedDocs.size < options.maxDocs
      ) {
        const full = await this.loadFullDocumentResult(r.documentId, r.similarity);
        if (full) {
          fullRows.push(full);
          expandedDocs.add(r.documentId);
          skipDocIds.add(r.documentId);
        }
      } else if (
        r.threadId &&
        !expandedThreads.has(r.threadId) &&
        expandedThreads.size < options.maxThreads
      ) {
        const full = await this.loadFullThreadResult(r.threadId, r.similarity);
        if (full) {
          fullRows.push(full);
          expandedThreads.add(r.threadId);
          skipThreadIds.add(r.threadId);
        }
      }
    }

    if (!fullRows.length) return results;

    const remainder = results.filter((r) => {
      if (r.documentId && skipDocIds.has(r.documentId)) return false;
      if (r.threadId && skipThreadIds.has(r.threadId)) return false;
      return true;
    });

    return [...fullRows, ...remainder];
  }

  private async loadFullDocumentResult(
    documentId: string,
    similarity: number
  ): Promise<RetrievalResult | null> {
    try {
      const kbDoc = await knowledgeBaseService.getKnowledgeBaseItem(documentId);
      let fullText = kbDoc?.content?.trim() || '';
      const docTitle = kbDoc?.title || 'Document';
      const userId = await this.getCurrentUserId();

      // Prefer embedding concat when KB body is missing or suspiciously short (stub / topic name)
      if (fullText.length < SHORT_KB_CONTENT_CHARS) {
        const embRows = await this.fetchEmbeddingChunksByDocumentId(documentId, userId);
        const concat = this.concatenateChunks(embRows, MANIFEST_DIRECT_CONTENT_MAX_CHARS);
        if (concat && concat.chunkText.trim().length > fullText.length) {
          return {
            ...concat,
            similarity,
            documentTitle: concat.documentTitle || docTitle,
            metadata: {
              ...concat.metadata,
              source: 'rag_full_document_expand',
              fullContentExpanded: true,
              documentTitle: concat.documentTitle || docTitle,
            },
          };
        }
        if (!fullText) return null;
      }

      if (fullText.length > MANIFEST_DIRECT_CONTENT_MAX_CHARS) {
        fullText = fullText.slice(0, MANIFEST_DIRECT_CONTENT_MAX_CHARS) + '\n...[truncated]';
      }
      return {
        documentId,
        chunkText: fullText,
        similarity,
        documentTitle: docTitle,
        metadata: {
          source: 'rag_full_document_expand',
          fullContentExpanded: true,
          documentTitle: docTitle,
        },
        sourceType: 'document',
      };
    } catch (e) {
      console.warn(`[ragService] loadFullDocumentResult failed for ${documentId}:`, e);
      return null;
    }
  }

  private async loadFullThreadResult(
    threadId: string,
    similarity: number
  ): Promise<RetrievalResult | null> {
    try {
      const thread = await chatThreadsService.getChatThread(threadId);
      if (!thread) return null;
      let fullText = this.threadMessagesToText(thread.messages);
      if (!fullText.trim()) return null;
      if (fullText.length > MANIFEST_DIRECT_CONTENT_MAX_CHARS) {
        fullText = fullText.slice(0, MANIFEST_DIRECT_CONTENT_MAX_CHARS) + '\n...[truncated]';
      }
      const threadTitle = thread.title || 'Untitled conversation';
      return {
        threadId,
        chunkText: fullText,
        similarity,
        threadTitle,
        metadata: {
          source: 'rag_full_thread_expand',
          fullContentExpanded: true,
          threadTitle,
          spaceId: thread.linkedSpaceIds?.[0],
          topicId: thread.topicId,
        },
        sourceType: 'thread',
      };
    } catch (e) {
      console.warn(`[ragService] loadFullThreadResult failed for ${threadId}:`, e);
      return null;
    }
  }

  /**
   * Merge document and thread results by similarity (desc) for combined RAG context
   */
  private mergeBySimilarity(
    docResults: RetrievalResult[],
    threadResults: RetrievalResult[]
  ): RetrievalResult[] {
    const combined = [...docResults, ...threadResults];
    return combined.sort((a, b) => (b.similarity ?? 0) - (a.similarity ?? 0));
  }

  /**
   * Get embedding statistics for user
   */
  async getEmbeddingStats(): Promise<EmbeddingStats> {
    const userId = await this.getCurrentUserId();

    const { data, error } = await supabase.rpc('get_embedding_stats', {
      p_user_id: userId,
    });

    if (error) {
      console.error('Error fetching embedding stats:', error);
      return {
        totalDocuments: 0,
        totalChunks: 0,
        avgChunksPerDoc: 0,
      };
    }

    const stats = data?.[0] || {};
    return {
      totalDocuments: parseInt(stats.total_documents) || 0,
      totalChunks: parseInt(stats.total_chunks) || 0,
      avgChunksPerDoc: parseFloat(stats.avg_chunks_per_doc) || 0,
      oldestEmbedding: stats.oldest_embedding,
      newestEmbedding: stats.newest_embedding,
    };
  }

  /**
   * Check if document has embeddings
   */
  async hasEmbeddings(documentId: string): Promise<boolean> {
    const userId = await this.getCurrentUserId();

    const { count, error } = await supabase
      .from('document_embeddings')
      .select('*', { count: 'exact', head: true })
      .eq('document_id', documentId)
      .eq('user_id', userId);

    if (error) {
      console.error('Error checking embeddings:', error);
      return false;
    }

    return (count || 0) > 0;
  }

  /**
   * Get embedding status for multiple documents in one query (e.g. for Library/Grid).
   * Returns a map of documentId -> true if embedded, false otherwise.
   */
  async getEmbeddingStatusForDocuments(documentIds: string[]): Promise<Record<string, boolean>> {
    if (documentIds.length === 0) return {};
    const userId = await this.getCurrentUserId();
    return presenceForIds(documentIds, async (batch) => {
      const { data, error } = await supabase.rpc('embedding_ids_present', {
        p_user_id: userId,
        p_kind: 'document',
        p_ids: batch,
      });
      if (error) {
        console.error('Error fetching embedding status:', error);
        return [];
      }
      return coverageIdsFromRpc(data);
    });
  }

  /**
   * Re-embed document (useful after content updates)
   */
  async reembedDocument(
    documentId: string,
    onProgress?: (progress: ProcessingProgress) => void
  ): Promise<void> {
    await this.embedDocument(documentId, onProgress, { forceReindex: true });
  }

  /**
   * Batch re-embed multiple documents
   */
  async reembedDocuments(
    documentIds: string[],
    onProgress?: (progress: ProcessingProgress) => void
  ): Promise<void> {
    for (const documentId of documentIds) {
      try {
        await this.reembedDocument(documentId, onProgress);
      } catch (error) {
        console.error(`Failed to re-embed document ${documentId}:`, error);
      }
    }
  }

  /**
   * Get retrieval context for RAG
   * Returns formatted context string for LLM
   */
  async getRetrievalContext(
    query: string,
    options: {
      spaceId?: string;
      limit?: number;
      similarityThreshold?: number;
    } = {}
  ): Promise<string> {
    const results = await this.search(query, options);

    if (results.length === 0) {
      return '';
    }

    // Resolve space/topic names so context shows where each chunk came from
    const { resolveSpaceTopicNames } = await import('./spaceTopicNameResolver');
    const { spaceNames, topicNames } = await resolveSpaceTopicNames(
      results.map((r) => r.metadata?.spaceId as string | undefined),
      results.map((r) => r.metadata?.topicId as string | undefined)
    );

    // Format results as context
    const contextParts = results.map((result, idx) => {
      const title = result.documentTitle || 'Untitled Document';
      const spaceName = result.metadata?.spaceId ? spaceNames.get(result.metadata.spaceId) : undefined;
      const topicName = result.metadata?.topicId ? topicNames.get(result.metadata.topicId) : undefined;
      const origin = [
        spaceName ? `Space: ${spaceName}` : null,
        topicName ? `Topic: ${topicName}` : null,
      ]
        .filter(Boolean)
        .join(' · ');
      const similarity = (result.similarity * 100).toFixed(1);
      return `[Source ${idx + 1}: ${title}${origin ? ` — ${origin}` : ''} (${similarity}% relevant)]\n${result.chunkText}`;
    });

    return contextParts.join('\n\n---\n\n');
  }
}

// Export singleton instance
export const ragService = new RAGService();
